import type { CourseDetail } from "@/lib/types/course/detail"
import { getBaseUrl } from "@/lib/utils/api"

interface GetCoursesParams {
	page?: number
	per_page?: number
	categoryId?: string
	search?: string
	sort?: 'most_learned' | 'highest_rated' | 'newest' | 'price_down' | 'price_low_to_high' | 'price_high_to_low' | 'free' | 'upcoming'
}

interface PaginatedResponse {
	data: CourseDetail[]
	paginate: {
		total: number
		per_page: number
		current_page: number
		last_page: number
		next: number | null
		prev: number | null
	}
}

class CourseServiceError extends Error {
	constructor(
		message: string,
		public readonly cause?: unknown,
		public readonly statusCode?: number
	) {
		super(message)
		this.name = "CourseServiceError"
	}
}

export async function getCourses(params?: GetCoursesParams): Promise<PaginatedResponse> {
	try {
		const queryParams = new URLSearchParams()

		queryParams.append("page", (params?.page || 1).toString())
		queryParams.append("per_page", (params?.per_page || 8).toString())

		if (params?.categoryId) {
			queryParams.append("category_id", params.categoryId)
		}
		if (params?.search) {
			queryParams.append("search", params.search)
		}
		if (params?.sort) {
			queryParams.append("sort", params.sort)
		}

		const baseUrl = getBaseUrl()
		const queryString = queryParams.toString()
		const url = `${baseUrl}/api/courses${queryString ? `?${queryString}` : ""}`

		const response = await fetch(url, { cache: "no-store" })

		if (!response.ok) {
			throw new CourseServiceError(
				"Failed to fetch courses",
				undefined,
				response.status
			)
		}

		const json = await response.json()
		return json as PaginatedResponse
	} catch (error) {
		if (error instanceof CourseServiceError) throw error

		console.error("Error fetching courses:", error)
		throw new CourseServiceError(
			"Failed to fetch courses",
			error
		)
	}
}
